import { useState, useEffect, useMemo, useRef } from "react";
import { RichText } from "@/components/RichText";
import axios from "axios";
import { useAuth, API } from "@/App";
import { X, Megaphone, PartyPopper, AlertTriangle, ExternalLink } from "lucide-react";

const STYLES = {
  info: { wrap: "bg-sky-50 border-sky-200 text-sky-900", icon: <Megaphone className="w-5 h-5 text-sky-500" /> },
  celebration: { wrap: "bg-amber-50 border-amber-300 text-amber-900", icon: <PartyPopper className="w-5 h-5 text-amber-500" /> },
  alert: { wrap: "bg-red-50 border-red-200 text-red-900", icon: <AlertTriangle className="w-5 h-5 text-red-500" /> }
};

export const BannerView = ({ banner, imageUrl, onDismiss, testId, linkTestId, dismissTestId }) => {
  const s = STYLES[banner.style] || STYLES.info;
  const [zoom, setZoom] = useState(false);
  return (
    <>
    <div className={`relative border rounded-xl p-4 flex flex-col sm:flex-row gap-4 items-start ${s.wrap}`} data-testid={testId}>
      {imageUrl
        ? <button type="button" onClick={() => setZoom(true)} className="w-full sm:w-auto flex-shrink-0 cursor-zoom-in" data-testid={`${testId}-image-link`} aria-label="View image full screen">
            <img src={imageUrl} alt={banner.title || ""} className="w-full sm:w-auto sm:max-w-md max-h-80 rounded-lg object-contain bg-white/60 transition-transform duration-200 hover:scale-[1.02]" data-testid={`${testId}-image`} />
          </button>
        : <div className="mt-0.5 flex-shrink-0">{s.icon}</div>}
      <div className="flex-1 min-w-0 pr-6">
        <div className="font-bold text-lg">{banner.title || "Banner title"}</div>
        {banner.message && <RichText html={banner.message} className="text-sm mt-1 opacity-90" data-testid={`${testId}-message`} />}
        {banner.link_url && (
          <a href={banner.link_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs font-semibold underline mt-1.5"
            data-testid={linkTestId}>
            {banner.link_label || "Learn more"}<ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
      {banner.dismissible && (
        <button onClick={onDismiss} className={`absolute top-3 right-3 opacity-50 ${onDismiss ? "hover:opacity-100" : "cursor-default"}`} data-testid={dismissTestId} aria-label="Dismiss">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
    {zoom && <BannerLightbox src={imageUrl} alt={banner.title} onClose={() => setZoom(false)} testId={testId} />}
    </>
  );
};

const BannerLightbox = ({ src, alt, onClose, testId }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200" onClick={onClose} data-testid={`${testId}-lightbox`}>
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" aria-label="Close" data-testid={`${testId}-lightbox-close`}>
        <X className="w-5 h-5" />
      </button>
      <img src={src} alt={alt || ""} className="max-w-full max-h-[92vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} data-testid={`${testId}-lightbox-image`} />
    </div>
  );
};

export const StudentBanners = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [banners, setBanners] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("dismissed_banners") || "[]"); } catch { return []; }
  });
  const [images, setImages] = useState({});
  const urlsRef = useRef([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/banners`, { headers });
        setBanners(res.data || []);
        // Prune stale dismissed ids so localStorage doesn't grow forever
        const liveIds = new Set((res.data || []).map(b => b.banner_id));
        setDismissed(prev => {
          const pruned = prev.filter(id => liveIds.has(id));
          if (pruned.length !== prev.length) localStorage.setItem("dismissed_banners", JSON.stringify(pruned));
          return pruned;
        });
        (res.data || []).filter(b => b.image_file_id).forEach(async (b) => {
          try {
            const r = await axios.get(`${API}/files/${b.image_file_id}/download`, { headers, responseType: "blob" });
            const u = URL.createObjectURL(r.data);
            urlsRef.current.push(u);
            setImages(prev => ({ ...prev, [b.banner_id]: u }));
          } catch (e) { /* image optional */ }
        });
      } catch (e) { /* banners optional */ }
    })();
    const urls = urlsRef.current;
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [headers]);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("dismissed_banners", JSON.stringify(next));
  };

  const visible = banners.filter(b => !(b.dismissible && dismissed.includes(b.banner_id)));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-3 mb-6" data-testid="student-banners">
      {visible.map(b => (
        <BannerView
          key={b.banner_id}
          banner={b}
          imageUrl={images[b.banner_id]}
          onDismiss={b.dismissible ? () => dismiss(b.banner_id) : undefined}
          testId={`banner-${b.banner_id}`}
          linkTestId={`banner-link-${b.banner_id}`}
          dismissTestId={`banner-dismiss-${b.banner_id}`}
        />
      ))}
    </div>
  );
};
